import type {
  CompressionRequest,
  WorkerFinalResponse,
  WorkerResponse,
} from '../types/worker-messages';

/** 압축 완료 전에 도착하는 썸네일. 태스크를 종료시키지 않는다. */
type PreviewHandler = (
  thumbnailBuffer: ArrayBuffer,
  contentType: string,
) => void;

interface QueuedTask {
  request: CompressionRequest;
  resolve: (response: WorkerFinalResponse) => void;
  reject: (error: Error) => void;
  onPreview?: PreviewHandler;
  canceled?: boolean;
}

export class WorkerPool {
  private workers: Worker[] = [];
  private busy: Set<number> = new Set();
  private queue: QueuedTask[] = [];
  private activeTasks: Map<number, QueuedTask> = new Map();
  private terminated = false;

  private readonly maxSize: number;

  constructor(
    private createWorker: () => Worker,
    poolSize?: number,
  ) {
    // 워커를 미리 다 만들지 않는다. 워커마다 pica/WASM 을 로드하므로, 업로드
    // 모달을 열기만 해도 쓰지 않을 초기화 비용과 메모리를 풀사이즈만큼 치른다.
    // 실제로 동시 처리가 필요해질 때 하나씩 늘린다(findFreeWorker).
    this.maxSize = poolSize ?? Math.max(2, navigator.hardwareConcurrency - 1);
  }

  public run(
    request: CompressionRequest,
    onPreview?: PreviewHandler,
  ): Promise<WorkerFinalResponse> {
    if (this.terminated) {
      return Promise.reject(new Error('이미지 업로드가 취소되었습니다.'));
    }

    return new Promise((resolve, reject) => {
      const task: QueuedTask = { request, resolve, reject, onPreview };
      const freeIndex = this.findFreeWorker();
      if (freeIndex !== -1) {
        this.dispatch(freeIndex, task);
      } else {
        this.queue.push(task);
      }
    });
  }

  public cancel(requestId: string): boolean {
    const queueIndex = this.queue.findIndex(
      (task) => task.request.id === requestId,
    );
    if (queueIndex !== -1) {
      const [task] = this.queue.splice(queueIndex, 1);
      task.reject(new Error('이미지 업로드가 취소되었습니다.'));
      return true;
    }

    for (const [, task] of this.activeTasks.entries()) {
      if (task.request.id === requestId) {
        task.canceled = true;
        return true;
      }
    }

    return false;
  }

  public terminate(): void {
    this.terminated = true;
    this.workers.forEach((w) => w.terminate());
    this.workers = [];
    this.activeTasks.forEach((task) =>
      task.reject(new Error('이미지 업로드가 취소되었습니다.')),
    );
    this.activeTasks.clear();
    this.queue.forEach((t) =>
      t.reject(new Error('이미지 업로드가 취소되었습니다.')),
    );
    this.queue = [];
  }

  public get pendingCount(): number {
    return this.queue.length;
  }

  public get activeCount(): number {
    return this.busy.size;
  }

  private findFreeWorker(): number {
    for (let i = 0; i < this.workers.length; i++) {
      if (!this.busy.has(i)) return i;
    }
    if (this.workers.length < this.maxSize) {
      this.workers.push(this.createWorker());
      return this.workers.length - 1;
    }
    return -1;
  }

  private dispatch(index: number, task: QueuedTask): void {
    this.busy.add(index);
    this.activeTasks.set(index, task);
    const worker = this.workers[index];

    const handler = (e: MessageEvent<WorkerResponse>) => {
      // 미리보기는 중간 메시지다 — 태스크를 끝내지 않고 워커도 계속 점유한다.
      if (e.data.type === 'compress-preview') {
        if (!task.canceled) {
          task.onPreview?.(e.data.thumbnailBuffer, e.data.thumbnailContentType);
        }
        return;
      }

      worker.removeEventListener('message', handler);
      worker.removeEventListener('error', errorHandler);
      this.activeTasks.delete(index);
      this.busy.delete(index);

      if (task.canceled) {
        task.reject(new Error('이미지 업로드가 취소되었습니다.'));
      } else {
        task.resolve(e.data);
      }
      this.processQueue();
    };

    const errorHandler = (e: ErrorEvent) => {
      worker.removeEventListener('message', handler);
      worker.removeEventListener('error', errorHandler);
      this.activeTasks.delete(index);
      this.busy.delete(index);
      task.reject(new Error(e.message));

      // Replace broken worker
      worker.terminate();
      this.workers[index] = this.createWorker();
      this.processQueue();
    };

    worker.addEventListener('message', handler);
    worker.addEventListener('error', errorHandler);

    const { buffer } = task.request;
    worker.postMessage(task.request, [buffer]);
  }

  private processQueue(): void {
    while (this.queue.length > 0) {
      const freeIndex = this.findFreeWorker();
      if (freeIndex === -1) break;
      const next = this.queue.shift()!;
      this.dispatch(freeIndex, next);
    }
  }
}
